"use client";

/**
 * Live preview card that floats above the URL input on the homepage
 * hero and on /analyze. As soon as the user pastes / types a
 * recognizable bina.az / emlak.az / ev10.az / arenda.az URL, the
 * card debounces, hits
 * `/api/listing-preview`, and renders a compact summary: thumbnail
 * + middle column with rooms/area/location + price anchored to the
 * right so the card reads as a balanced unit instead of leaving a
 * column of dead space on the right.
 *
 * Collapse / expand uses a CSS-grid `grid-template-rows: 0fr | 1fr`
 * transition (the modern "animate to height: auto" trick) so when
 * the URL is cleared the slot smoothly shrinks instead of yanking
 * the input row up by ~90 px in one frame. The wrapper is ALWAYS
 * rendered to give the transition a stable target.
 *
 * Used in two contexts:
 *   - `variant="hero"` - homepage hero, white card on the dark
 *     banner image. Higher contrast styling.
 *   - `variant="form"` - /analyze input card, light grey card that
 *     reads as a sibling of the input below.
 */

import { useEffect, useRef, useState } from "react";
import { Building2, Maximize2, MapPin, CheckCircle2, History } from "lucide-react";
import { fetchListingPreview, peekListingPreview, formatPrice, type ListingPreviewResponse } from "@/lib/api";
import { looksLikeListingUrl } from "@/lib/listing-url";
import { useT } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";
import Photo from "@/components/Photo";

const DEBOUNCE_MS = 450;

export interface UrlPreviewCardProps {
  url: string;
  variant?: "hero" | "form";
  /** Header chip variant.
   *   - "found" (default): green "Listing found · <source>" - the
   *     fresh-paste case on the homepage / analyze / compare URL slots.
   *   - "history": grey "From your history" - the compare-slot
   *     case when the user picked a row from the history modal so
   *     the wording matches the user's mental model (it's not "found
   *     for the first time," it's "the analysis you already ran"). */
  sourceLabel?: "found" | "history";
  /** When true, render the skeleton placeholder immediately even
   *  before the URL has been written to ``url``. Used by the
   *  homepage hero so the slot is reserved from the first paint
   *  while the prefill is in flight - the input doesn't jump down
   *  when the demo URL resolves and the live preview fires. */
  eagerSkeleton?: boolean;
  className?: string;
  /** Makes the whole card a button that runs this (the analysis). */
  onActivate?: () => void;
}

export default function UrlPreviewCard({
  url,
  variant = "form",
  sourceLabel = "found",
  eagerSkeleton = false,
  className,
  onActivate,
}: UrlPreviewCardProps) {
  const t = useT();
  const [data, setData] = useState<ListingPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const inFlightRef = useRef<AbortController | null>(null);
  const lastUrlRef = useRef<string>("");

  useEffect(() => {
    const trimmed = url.trim();
    // Empty or doesn't look like a listing URL - clear and bail.
    if (!looksLikeListingUrl(trimmed)) {
      inFlightRef.current?.abort();
      inFlightRef.current = null;
      lastUrlRef.current = "";
      setData(null);
      setLoading(false);
      return;
    }
    // Same URL as last successful lookup - already showing the right
    // card, nothing to do.
    if (trimmed === lastUrlRef.current && data?.ok) return;

    // Session cache hit (the prefilled demo URL after any SPA
    // navigation): render the card in the same frame - no debounce,
    // no skeleton, no flash.
    const cached = peekListingPreview(trimmed);
    if (cached) {
      lastUrlRef.current = trimmed;
      setData(cached);
      setLoading(false);
      return;
    }

    // Flip loading immediately so the skeleton stays visible during
    // the 450 ms debounce window. The fetch itself still debounces;
    // we just don't want a "skeleton → blank → skeleton → data"
    // flash when the eager-skeleton path hands off to the live one.
    setLoading(true);

    const handle = window.setTimeout(() => {
      inFlightRef.current?.abort();
      const ctrl = new AbortController();
      inFlightRef.current = ctrl;
      fetchListingPreview(trimmed, { signal: ctrl.signal })
        .then((res) => {
          if (ctrl.signal.aborted) return;
          lastUrlRef.current = trimmed;
          setData(res);
        })
        .catch((err) => {
          if (ctrl.signal.aborted) return;
          if (process.env.NODE_ENV !== "production") {
            console.warn("listing-preview failed", err);
          }
          setData({ ok: false });
        })
        .finally(() => {
          if (ctrl.signal.aborted) return;
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
    // `data` intentionally omitted - including it would re-fire the
    // effect every time we set state, kicking off a fresh debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const cardStyles = variant === "hero"
    ? "bg-white/95 backdrop-blur-sm border-white/40 text-gray-900 shadow-xl"
    : "bg-white border-gray-200 text-gray-900 shadow-sm";

  // `intentOpen` is what the data layer wants - true if we have a
  // resolved result OR a debounce-fired in-flight request, OR the
  // caller asked us to render an eager skeleton (homepage hero
  // during prefill - reserves the slot so the input doesn't jump
  // when the prefill resolves a microsecond later). The outer
  // grid uses this to drive the 1fr/0fr transition.
  const hasData = !!data?.ok;
  const showSkeleton = (loading && !hasData) || (eagerSkeleton && !hasData);
  const intentOpen = hasData || showSkeleton;

  // `content` is what's *actually mounted* in the slot. Critical:
  // we keep the previous content mounted while the close transition
  // is running (~300 ms). Without this, React would unmount the
  // child the moment intentOpen flipped to false, the grid row's
  // intrinsic content size would collapse to 0, and the 1fr→0fr
  // transition would skip - producing an instant jump instead of
  // a slide. The setTimeout that clears `content` only fires once
  // the animation has finished.
  type Display =
    | { kind: "closed" }
    | { kind: "skeleton" }
    | { kind: "data"; data: ListingPreviewResponse; url: string };
  const [content, setContent] = useState<Display>({ kind: "closed" });

  useEffect(() => {
    if (hasData && data) {
      // Capture the URL that resolved to this preview alongside the
      // data so the source link still points at the *right* listing
      // even during the close animation when the input has been
      // cleared but the card is still on its way out.
      setContent({ kind: "data", data, url: url.trim() });
      return;
    }
    if (showSkeleton) {
      setContent({ kind: "skeleton" });
      return;
    }
    // No content desired and the slot still has something - schedule
    // the unmount to land AFTER the close transition completes.
    if (content.kind !== "closed") {
      const t = window.setTimeout(() => setContent({ kind: "closed" }), 320);
      return () => window.clearTimeout(t);
    }
  }, [hasData, showSkeleton, data, content.kind, url]);

  return (
    <div
      aria-hidden={!intentOpen}
      className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${className ?? ""}`}
      style={{
        gridTemplateRows: intentOpen ? "1fr" : "0fr",
        opacity: intentOpen ? 1 : 0,
      }}
    >
      <div className="overflow-hidden min-h-0">
        {/* `pb-3` ON the inner row gives the visible gap to the input
            below when open. It collapses to 0 along with the row so
            the gap animates away too. */}
        <div className={content.kind !== "closed" ? "pb-3" : "pb-0"}>
          {content.kind === "skeleton" && <PreviewSkeleton cardStyles={cardStyles} />}
          {content.kind === "data" && (
            <PreviewCard
              data={content.data}
              url={content.url}
              cardStyles={cardStyles}
              sourceLabel={sourceLabel}
              t={t}
              onActivate={onActivate}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewSkeleton({ cardStyles }: { cardStyles: string }) {
  return (
    <div
      className={`rounded-2xl border p-3 sm:p-3.5 flex items-center gap-3 ${cardStyles}`}
      aria-busy="true"
    >
      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-gray-200 animate-pulse shrink-0" />
      <div className="flex-1 min-w-0 space-y-2">
        <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3" />
        <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
      </div>
      <div className="w-20 h-8 rounded bg-gray-200 animate-pulse shrink-0" />
    </div>
  );
}

function PreviewCard({
  data,
  url,
  cardStyles,
  sourceLabel,
  t,
  onActivate,
}: {
  data: ListingPreviewResponse;
  url: string;
  cardStyles: string;
  sourceLabel: "found" | "history";
  t: ReturnType<typeof useT>;
  onActivate?: () => void;
}) {
  const siteSource = data.source ?? "";
  const priceSuffix = data.mode === "rent" ? t("result.permonth") : "AZN";
  // A plot is sized in sot, not m²; a garage, an office or a shop has
  // no model, so the card says so and does not run anything (owner,
  // 2026-10-11: "lands have the sot unit; we should not fetch the garage").
  const kind = data.property_type ?? "apartment";
  const isLand = kind === "land";
  const unsupported = !["apartment", "house", "land"].includes(kind);
  const activate = unsupported ? undefined : onActivate;
  // Header chip: green check + "Listing found" for fresh-paste cases,
  // grey History icon + "From your history" when the row was picked
  // from the user's analysis history. Same overall layout - just the
  // icon, colour, and label string differ.
  const headerKey: TranslationKey =
    sourceLabel === "history" ? "preview.fromhistory" : "preview.found";
  const HeaderIcon = sourceLabel === "history" ? History : CheckCircle2;
  const headerColor =
    sourceLabel === "history" ? "text-gray-600" : "text-emerald-700";

  return (
    <div
      // The card reads as a button (owner, 2026-10-04: people tap it);
      // with an activator it is one and runs the same analysis the
      // submit button does.
      role={activate ? "button" : undefined}
      tabIndex={activate ? 0 : undefined}
      onClick={activate}
      onKeyDown={activate ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(); } } : undefined}
      className={`rounded-2xl border p-3 sm:p-3.5 flex items-center gap-3 ${cardStyles} ${activate ? "cursor-pointer transition-colors hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500" : ""}`}
    >
      {/* Thumbnail. Plain <img> rather than next/image - listing
          photo hosts (bina.azstatic.com, various emlak.az
          subdomains) aren't all in next.config remotePatterns and
          we'd rather show a slightly less optimised thumb than fail
          the render. */}
      {data.thumbnail_url ? (
        // eslint-disable-next-line @next/next/no-img-element
        <Photo src={data.thumbnail_url} alt="" aria-hidden frame="w-14 h-14 sm:w-16 sm:h-16 rounded-lg shrink-0" />
      ) : (
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
          <Building2 className="w-6 h-6 text-gray-400" />
        </div>
      )}

      <div className="flex-1 min-w-0 text-left">
        <div className={`flex items-center gap-1.5 mb-1 text-[10px] font-semibold ${headerColor}`}>
          <HeaderIcon className="w-3 h-3" />
          <span>{t(headerKey)}</span>
          {siteSource && url && (
            // Clickable source - opens the original listing in a
            // new tab so the user can sanity-check the preview
            // against the real page before spending a token on
            // analysis. Underline-on-hover keeps the chrome quiet
            // in the resting state.
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 normal-case tracking-normal font-normal hover:text-gray-900 hover:underline underline-offset-2 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              · {siteSource}
            </a>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] sm:text-xs text-gray-500">
          {!isLand && data.room_count != null && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <Building2 className="w-3 h-3 text-gray-400" />
              {data.room_count} {t("preview.rooms")}
            </span>
          )}
          {isLand && data.land_area_sot != null ? (
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <Maximize2 className="w-3 h-3 text-gray-400" />
              {Math.round(data.land_area_sot * 10) / 10} {t("preview.sot")}
            </span>
          ) : !isLand && data.area_m2 != null ? (
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <Maximize2 className="w-3 h-3 text-gray-400" />
              {Math.round(data.area_m2)} m²
            </span>
          ) : null}
          {data.location_name && (
            <span className="inline-flex items-center gap-1 truncate min-w-0">
              <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
              <span className="truncate">{data.location_name}</span>
            </span>
          )}
        </div>
        {unsupported && (
          <p className="mt-1 text-[11px] font-medium text-amber-700">{t("preview.unsupported")}</p>
        )}
      </div>

      {/* Price on the right - balances the visual weight of the
          thumbnail on the left so the card no longer leaves dead
          space past the location chip. */}
      {data.price != null && (
        <div className="text-right shrink-0 pl-2">
          <div className="text-base sm:text-lg font-extrabold text-gray-900 leading-none whitespace-nowrap tabular-nums">
            {formatPrice(data.price)}
          </div>
          <div className="text-[10px] sm:text-[11px] font-medium text-gray-400 mt-0.5">
            {priceSuffix}
          </div>
        </div>
      )}
    </div>
  );
}
